export const ALLOWED_QUESTION_TYPES = [
  'single_select',
  'multiple_select',
  'text',
] as const;

export enum AssignmentQuestionCategory {
  SINGLE_SELECT = 'single_select',
  MULTIPLE_SELECT = 'multiple_select',
  SUBJECTIVE = 'text',
}

export type AssignmentQuestionType = `${AssignmentQuestionCategory}`;

export const ALLOWED_QUESTION_FILE_TYPES = [
  'image',
  'video',
  'docx',
  'pdf',
] as const;

export enum AllowedFileInQuestion {
  IMAGE = 'image',
  VIDEO = 'video',
  DOCX = 'docx',
  PDF = 'pdf',
}

export type AllowedFileTypeInQuestion = `${AllowedFileInQuestion}`;

// Option for questions
export interface IAssignmentQuestionOption {
  type: 'text' | 'image';
  text?: string;
  image?: string;
}

export type AssignmentQuestionOptionType =
  `${IAssignmentQuestionOption['type']}`;

export interface IAssignmentUser {
  _id: string;
  name: string;
  profilePicUrl: string;
}
export interface IAssignmentQuestion {
  _id: string;
  type: AssignmentQuestionType;
  images?: string[];
  question: string;
  marks: number;
  order: number;
  options?: IAssignmentQuestionOption[];
  correctOptions?: number[];
  wordLimit?: number;
  allowFileUploads?: boolean;
  allowedFileTypes?: AllowedFileTypeInQuestion[];
  maxFileCount?: number; // max number of files allowed
  maxFileSize?: number; // in bytes
  createdAt?: string;
  updatedAt?: string;
}

export interface ICourseAssignment {
  _id: string;
  course: ICourseDetailsInAssignment;
  chapter: IChapterDetailsInAssignment;
  creator: string;
  title: string;
  description: string;
  retaking: boolean;
  questions: IAssignmentQuestion[];
  passingPercentage: number;
  enforcePassingPercentage: boolean;
  enableCountdown: boolean;
  countdown?: number;
  totalMarks: number;
  isPublished: boolean;
  ongoingAssignmentsCount?: boolean;
  noOfQuestions: number;
  noOfSubmissions?: number;
  recentSubmissions?: IRecentAssignmentSubmission[];
  canResume?: boolean;
  totalMarksObtained?: number;
  scoreInPercentage?: number;
  passed?: boolean;
  submittedAt?: string;
  reviewedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IRecentAssignmentSubmission {
  assignment: string;
  creator: string;
  submittedAt: string;
  reviewedAt: string;
  user: IRecentAssignmentSubmissionUser;
}

export interface IRecentAssignmentSubmissionUser {
  _id: string;
  name: string;
  profilePicUrl: string;
  profilePicUrlUncompressed?: string;
}

export interface ICourseDetailsInAssignment {
  _id: string;
  title: string;
  description: string;
  creator: string;
}

export interface IChapterDetailsInAssignment {
  _id: string;
  title: string;
}

export interface IQuestionAnswerAttachment {
  url: string;
  name: string; // file name
  type: AllowedFileTypeInQuestion;
  size: number; // in bytes
  // mimeType: AcceptedDocFileType;
  mimeType: string;
  key: string; // s3 key
}

export interface IObtainedMarksForQuestion extends IAssignmentQuestion {
  obtainedMarks: number;
  selectedOptions?: number[];
  subjectiveAnswer?: string;
  attachments?: IQuestionAnswerAttachment[];
  remarks?: string[];
  submittedAt?: string;
  savedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IAssignmentSubmission {
  _id: string;
  assignment: string;
  creator: string;
  user: string;
  obtainedMarksForQuestions: IObtainedMarksForQuestion[];
  startedAt: string;
  startQuestionId: string;
  lastAttemptedQuestionId?: string;
  submittedAt?: string;
  reviewedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IStartAssignmentResponse extends IAssignmentSubmission {
  startQuestionId: string;
  lastAttemptedQuestionId: string;
}

export enum QuestionAttemptStatus {
  CORRECT = 'correct',
  WRONG = 'wrong',
  SKIPPED = 'skipped',
  ATTEMPTED = 'attempted',
}

export type QuestionAttemptStatusType = `${QuestionAttemptStatus}`;

export interface ISubmitQuestionAnswerRequest {
  attachments?: IQuestionAnswerAttachment[];
  selectedOptions?: number[];
  subjectiveAnswer?: string;
}
export interface IAssignmentSubmitAnswerResponse {
  attachments?: {
    url: string;
    name: string;
    type: string;
    size: number;
    mimeType: string;
    key: string;
  }[];
  selectedOptions?: number[];
  subjectiveAnswer?: string;
}
